import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faSearch,
  faFilter,
  faArrowsRotate,
  faEye,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  InputGroup,
  Form,
  Button,
  Badge,
  Modal,
} from "@themesberg/react-bootstrap";
import {
  adminGetAllTickets,
  getTicketById,
  adminUpdateTicketStatus,
} from "../../../pages/api";
import { MEDIA_URL } from "../../helper";

const STATUS_OPTIONS = ["Open", "In Progress", "Resolved", "Closed"];
const CATEGORY_OPTIONS = ["Payment", "Access", "KYC", "Technical"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High"];

const statusBadgeClass = (status) => {
  switch (status) {
    case "Open":
      return "bg-warning text-white border border-warning-subtle";
    case "In Progress":
      return "bg-info text-white border border-info-subtle";
    case "Resolved":
      return "bg-success text-white border border-success-subtle";
    case "Closed":
      return "bg-secondary text-white border border-secondary-subtle";
    default:
      return "bg-light text-dark border border-light";
  }
};

const priorityBadgeClass = (priority) => {
  switch (priority) {
    case "Low":
      return "bg-success text-white border border-success-subtle";
    case "Medium":
      return "bg-warning text-white border border-warning-subtle";
    case "High":
      return "bg-danger text-white border border-danger-subtle";
    default:
      return "bg-light text-dark border border-light";
  }
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination) => {
  const total = Number(pagination?.total || 0);
  const perPage = Number(pagination?.per_page || 10);
  const current = Math.max(1, Number(pagination?.current_page || 1));

  if (total === 0) return { start: 0, end: 0 };

  const start = (current - 1) * perPage + 1;
  const end = Math.min(current * perPage, total);
  return { start, end };
};

const emptyTicket = {
  id: null,
  ticket_id: "",
  subject: "",
  category: "",
  priority: "",
  status: "",
  description: "",
  attachment: "",
  created_at: "",
};

const normalizeFileUrl = (path) => {
  if (!path) return "N/A";
  const value = String(path).trim();

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${MEDIA_URL}/${value.replace(/^\/+/, "")}`.replace(
    /([^:]\/)\/+/g,
    "$1"
  );
};

export default function AdminSupportTicketList() {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(emptyTicket);
  const [updateForm, setUpdateForm] = useState({
    status: "",
    admin_remark: "",
  });

  const searchDebounceRef = useRef(null);
  const initialLoadRef = useRef(true);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchTickets = async (page = 1, overrides = {}) => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: overrides.per_page ?? pagination.per_page ?? 10,
        search: overrides.search ?? search,
        category: overrides.category ?? categoryFilter,
        priority: overrides.priority ?? priorityFilter,
        status: overrides.status ?? statusFilter,
      };

      const response = await adminGetAllTickets(params);
      const apiTickets = response?.data?.data?.tickets || [];
      const apiPagination = response?.data?.data?.pagination || {};

      setTickets(apiTickets);
      setPagination({
        current_page: Number(apiPagination.current_page || page),
        last_page: Number(apiPagination.last_page || 1),
        per_page: Number(apiPagination.per_page || params.per_page || 10),
        total: Number(apiPagination.total || 0),
      });
    } catch (error) {
      // console.error("Failed to fetch tickets:", error);
      toast.error("Unable to load ticket list.");
      setTickets([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketById = async (id) => {
    setDetailLoading(true);
    try {
      const response = await getTicketById(id);
      const data = response?.data?.data || emptyTicket;

      setSelectedTicket({
        id: data?.id ?? id,
        ticket_id: data?.ticket_id || "N/A",
        subject: data?.subject || "N/A",
        category: data?.category || "N/A",
        priority: data?.priority || "N/A",
        status: data?.status || "N/A",
        admin_remark: data?.admin_remark || "N/A",
        description: data?.description || "N/A",
        // attachment: data?.attachment || "N/A",
        attachment: data?.attachment ?? null,
        created_at: data?.created_at || "N/A",
      });

      return data;
    } catch (error) {
      // console.error("Failed to fetch ticket details:", error);
      toast.error("Unable to load ticket details.");
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchTickets(1, {
        search: "",
        category: "",
        priority: "",
        status: "",
      });
      return;
    }

    clearSearchDebounce();
    searchDebounceRef.current = setTimeout(() => {
      fetchTickets(1, {
        search,
        category: categoryFilter,
        priority: priorityFilter,
        status: statusFilter,
      });
    }, 300);

    return () => clearSearchDebounce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryFilter, priorityFilter, statusFilter]);

  const handleClearFilters = () => {
    setSearch("");
    setCategoryFilter("");
    setPriorityFilter("");
    setStatusFilter("");
    clearSearchDebounce();

    fetchTickets(1, {
      search: "",
      category: "",
      priority: "",
      status: "",
    });
  };

  const openViewModal = async (id) => {
    const data = await fetchTicketById(id);
    if (data) setViewModalOpen(true);
  };

  const openUpdateModal = async (id) => {
    const data = await fetchTicketById(id);
    if (data) {
      setUpdateForm({
        status: data?.status || "",
        admin_remark: "",
      });
      setUpdateModalOpen(true);
    }
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTicket?.id) {
      toast.error("Ticket ID is missing.");
      return;
    }

    if (!updateForm.status) {
      toast.error("Please select ticket status.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        status: updateForm.status,
      };

      if (String(updateForm.admin_remark || "").trim()) {
        payload.admin_remark = updateForm.admin_remark.trim();
      }

      await adminUpdateTicketStatus(selectedTicket.id, payload);

      toast.success("Ticket status updated successfully.");
      setUpdateModalOpen(false);
      await fetchTickets(pagination.current_page, {
        search,
        category: categoryFilter,
        priority: priorityFilter,
        status: statusFilter,
      });
    } catch (error) {
      // console.error("Failed to update ticket status:", error);
      toast.error("Unable to update ticket status.");
    } finally {
      setSubmitting(false);
    }
  };

  const { start, end } = getShowingRange(pagination);
  const pageItems = getPageItems(pagination.current_page, pagination.last_page);

  return (
    <>
      <div className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2 py-4">
        <div>
          <h2 className="h4 mb-1">Admin Ticket List</h2>
          {/* <div className="text-muted small">View and manage support tickets.</div> */}
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-3 p-md-4">
          {/* <div className="d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-2 text-muted" />
                Filters
              </h5>
              <div className="text-muted small">Search by ticket details and narrow results using filters.</div>
            </div>

            <Button variant="outline-secondary" onClick={handleClearFilters} className="fw-semibold">
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              Reset Filters
            </Button>
          </div> */}

          <Row className="g-3">
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Category</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Categories</option>
                {CATEGORY_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Priority</Form.Label>
              <Form.Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Priorities</option>
                {PRIORITY_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ boxShadow: "none" }}
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fw-semibold">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ boxShadow: "none" }}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="mb-1">Raised Issues</h5>
              {/* <div className="text-muted small">Tickets fetched from backend with server-side filters.</div> */}
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  {/* <th>Attachment</th> */}
                  {/* <th>Created At</th> */}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      Loading tickets...
                    </td>
                  </tr>
                ) : tickets.length > 0 ? (
                  tickets.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.ticket_id || "N/A"}</td>
                      <td>{item.subject || "N/A"}</td>
                      <td>{item.category || "N/A"}</td>
                      <td>
                        <Badge className={`rounded-pill px-3 py-2 text-capitalize ${priorityBadgeClass(item.priority)}`}>
                          {item.priority || "N/A"}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(item.status)}`}>
                          {item.status || "N/A"}
                        </Badge>
                      </td>
                      {/* <td className="text-break">{item.attachment ? item.attachment : "N/A"}</td> */}
                      {/* <td>{item.created_at || "N/A"}</td> */}
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2 flex-nowrap">
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openViewModal(item.id)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                            {/* View */}
                          </Button>
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openUpdateModal(item.id)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                            {/* Update */}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      No tickets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchTickets(Math.max(1, pagination.current_page - 1), {
                    search,
                    category: categoryFilter,
                    priority: priorityFilter,
                    status: statusFilter,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {pageItems.map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchTickets(item, {
                        search,
                        category: categoryFilter,
                        priority: priorityFilter,
                        status: statusFilter,
                      });
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchTickets(Math.min(pagination.last_page, pagination.current_page + 1), {
                    search,
                    category: categoryFilter,
                    priority: priorityFilter,
                    status: statusFilter,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal show={viewModalOpen} onHide={() => setViewModalOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ticket Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center text-muted py-4">Loading ticket details...</div>
          ) : (
            <Row className="g-3">
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Ticket ID</div>
                    <div className="fw-semibold">{selectedTicket.ticket_id}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Subject</div>
                    <div className="fw-semibold">{selectedTicket.subject}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Category</div>
                    <div className="fw-semibold">{selectedTicket.category}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Priority</div>
                    <div className="fw-semibold">
                      <Badge className={`rounded-pill px-3 py-2 text-capitalize ${priorityBadgeClass(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Status</div>
                    <div>
                      <Badge className={`rounded-pill px-3 py-2 text-capitalize ${statusBadgeClass(selectedTicket.status)}`}>
                        {selectedTicket.status}
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Created At</div>
                    <div className="fw-semibold">{selectedTicket.created_at}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Description</div>
                    <div className="fw-semibold">{selectedTicket.description}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="border-1 shadow-sm rounded-4 h-100">
                  <Card.Body>
                    <div className="text-muted small">Admin Remark</div>
                    <div className="fw-semibold">{selectedTicket.admin_remark}</div>
                  </Card.Body>
                </Card>
              </Col>

              {selectedTicket?.attachment?.trim() && (
                <Col xs={12}>
                  <Card className="border-1 shadow-sm rounded-4 h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">Attachment</div>
                        <div className="mt-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            className="text-decoration-none"
                            onClick={() =>
                              window.open(
                                normalizeFileUrl(selectedTicket.attachment),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            View Document
                          </Button>
                        </div>
                        {/* <div className="text-break">{selectedTicket.attachment || "N/A"}</div> */}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </Modal.Body>
        {/* <Modal.Footer>
          <Button onClick={() => setViewModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>

      <Modal show={updateModalOpen} onHide={() => setUpdateModalOpen(false)} centered>
        <Form onSubmit={handleUpdateSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Ticket Status</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailLoading ? (
              <div className="text-center text-muted py-4">Loading ticket details...</div>
            ) : (
              <div className="d-grid gap-3">
                <div>
                  <div className="text-muted small mb-1">Ticket ID</div>
                  <div className="fw-semibold">{selectedTicket.ticket_id}</div>
                </div>

                <div>
                  <div className="text-muted small mb-1">Subject</div>
                  <div className="fw-semibold">{selectedTicket.subject}</div>
                </div>

                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={updateForm.status}
                    onChange={handleUpdateChange}
                    style={{ boxShadow: "none" }}
                  >
                    <option value="">Select status</option>
                    {STATUS_OPTIONS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label className="fw-semibold">Admin Remark</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="admin_remark"
                    value={updateForm.admin_remark}
                    onChange={handleUpdateChange}
                    placeholder="Add optional remark"
                    style={{ boxShadow: "none" }}
                  />
                </Form.Group>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setUpdateModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" className="act-btn" disabled={submitting || detailLoading}>
              {submitting ? "Updating..." : "Update Status"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}